import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Input from '../../../components/Input/Input';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import DatePickerComponent from '../../../components/layout/DatePicker';
import { useGetLocalidadesPrestadoresQuery } from '../../../services/periodicosApi';
import { useGetProvincesQuery } from '../../../services/companyApi';
import { useGetTiposObraQuery } from '../../../services/avisoObraApi';
import { EstadoAvisoObraEnum, getEstadoAvisoObraLabel } from '../consts/avisoObraStatesTypes';
import { useRoles } from '../../../hooks/useRoles';
import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import useEstablishmentSave from '../../Empresas/EstablishmentSave/hooks/useEstablishmentSave';

interface AvisoObraFiltersProps {
    extraFilters: any;
    handleChangeExtraFilters: (e: any) => void;
    isShowMoreFiltersOpen?: boolean;
}

const AvisoObraFilters = ({
    extraFilters,
    handleChangeExtraFilters,
    isShowMoreFiltersOpen
}: AvisoObraFiltersProps) => {
    const { t } = useTranslation('avisoObra');
    const { isAdminBO } = useRoles();

    const {
        provincesList,
        citiesList,
        selectedProvince,
        selectedCity,
        handleProvinceSelection,
        handleCitySelection,
        loadingProvinces,
        loadingCities
    } = useEstablishmentSave();

    const { data: tiposObraData, isLoading: isLoadingTiposObra } = useGetTiposObraQuery();
    const { contracts, isFetching: isFetchingContracts } = useContracts();
    const gridColSize = isAdminBO ? 2.4 : 3;

    const ESTADOS_FILTROS = [
        EstadoAvisoObraEnum.BORRADOR,
        EstadoAvisoObraEnum.RECIBIDO,
        EstadoAvisoObraEnum.EN_REVISION,
        EstadoAvisoObraEnum.PRESENTADO,
        EstadoAvisoObraEnum.FINALIZADO,
        EstadoAvisoObraEnum.CANCELADO,
        EstadoAvisoObraEnum.SUSPENDIDO,
        EstadoAvisoObraEnum.EN_EXTENSION,
        EstadoAvisoObraEnum.A_REVISAR
    ];

    let ESTADOS_AVISO_OBRA_OPTIONS = ESTADOS_FILTROS.map((estado) => ({
        id: estado,
        nombre: getEstadoAvisoObraLabel(estado)
    }));

    if (isAdminBO) {
        ESTADOS_AVISO_OBRA_OPTIONS = ESTADOS_AVISO_OBRA_OPTIONS.filter(
            (e) => e.id !== EstadoAvisoObraEnum.BORRADOR
        );
    }

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Grid container>
                    {isAdminBO && (
                        <Grid item xs={12} sm={6} md={gridColSize}>
                            <AutocompleteComponent
                                size="small"
                                label={t('filters.contract')}
                                name="idOrganismoCentral"
                                value={extraFilters?.idOrganismoCentral || null}
                                onChange={(value: any) =>
                                    handleChangeExtraFilters({
                                        target: { name: 'idOrganismoCentral', value }
                                    })
                                }
                                options={contracts.map((c: any) => ({
                                    id: c.id,
                                    nombre: `${c.nroContrato} - ${c.nombre}`
                                }))}
                                loading={isFetchingContracts}
                                templateLabel={(item: any) => item?.nombre}
                                clearable
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={gridColSize}>
                        <Input
                            size="small"
                            label={t('filters.nroObra')?.toString() || ''}
                            name="nroObra"
                            value={extraFilters?.nroObra || ''}
                            onChange={handleChangeExtraFilters}
                            clearable
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={gridColSize}>
                        <AutocompleteComponent
                            size="small"
                            label={t('filters.estado')}
                            name="estado"
                            value={extraFilters?.estado || null}
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'estado',
                                        value
                                    }
                                })
                            }
                            options={ESTADOS_AVISO_OBRA_OPTIONS}
                            templateLabel={(item: any) => item?.nombre}
                            clearable
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={gridColSize}>
                        <AutocompleteComponent
                            size="small"
                            label={t('filters.provincia')}
                            name="idProvincia"
                            value={extraFilters?.idProvincia}
                            onChange={(value: any) => {
                                handleProvinceSelection(value);
                                handleChangeExtraFilters({
                                    target: { name: 'idProvincia', value }
                                });
                            }}
                            options={provincesList?.data?.provincias || []}
                            loading={loadingProvinces}
                            templateLabel={(item: any) => item?.nombre}
                            clearable
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={gridColSize}>
                        <AutocompleteComponent
                            size="small"
                            label={t('filters.localidad')}
                            name="localidad"
                            value={extraFilters?.localidad}
                            onChange={(value: any) => {
                                handleCitySelection(value);
                                handleChangeExtraFilters({
                                    target: { name: 'localidad', value }
                                });
                            }}
                            options={selectedProvince ? citiesList?.data?.localidades || [] : []}
                            loading={loadingCities}
                            templateLabel={(item: any) => item?.nombre}
                            clearable
                            readOnly={!selectedProvince}
                            onMouseDownCapture={(e: any) => {
                                if (!selectedProvince) {
                                    e.preventDefault();
                                }
                            }}
                        />
                    </Grid>

                    {isShowMoreFiltersOpen && (
                        <>
                            <Grid item xs={12} sm={6} md={4}>
                                <AutocompleteComponent
                                    size="small"
                                    label={t('filters.tipoObra')}
                                    name="idTipoObra"
                                    value={extraFilters?.idTipoObra || null}
                                    onChange={(value: any) =>
                                        handleChangeExtraFilters({
                                            target: { name: 'idTipoObra', value }
                                        })
                                    }
                                    options={tiposObraData || []}
                                    loading={isLoadingTiposObra}
                                    templateLabel={(item: any) => item?.nombre}
                                    clearable
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <DatePickerComponent
                                    size="small"
                                    label={t('filters.fechaInicio')}
                                    name="fechaInicio"
                                    value={extraFilters?.fechaInicio || ''}
                                    onChange={(value: any) =>
                                        handleChangeExtraFilters({
                                            target: {
                                                name: 'fechaInicio',
                                                value
                                            }
                                        })
                                    }
                                    clearable
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <DatePickerComponent
                                    size="small"
                                    label={t('filters.fechaFin')}
                                    name="fechaFin"
                                    value={extraFilters?.fechaFin || ''}
                                    onChange={(value: any) =>
                                        handleChangeExtraFilters({
                                            target: {
                                                name: 'fechaFin',
                                                value
                                            }
                                        })
                                    }
                                    clearable
                                />
                            </Grid>
                        </>
                    )}
                </Grid>
            </Box>
        </>
    );
};

export default AvisoObraFilters;
